// Add or edit a single lookup value. On add, the user enters only the Hebrew
// label (+ days for terms) and the server generates the code. On edit, the code
// is shown read-only; label / days / sort are editable.
import { useState } from 'react';
import { settingsApi } from '../../api';
import he from '../../i18n/he';
import Modal from '../../components/Modal';
import { Field, TextInput } from '../../components/Form';
import { ErrorText } from '../../components/Loader';

export default function ValueModal({ listKey, hasDays, hasContext, value, onClose, onSaved }) {
  const isNew = !value.code;
  const [form, setForm] = useState({
    label_he: value.label_he || '',
    // A new term starts with NO days: a 0 nobody typed made שוטף + 30 due on the invoice
    // date (075). The days are asked for, and Save is refused without them.
    due_days: value.due_days != null ? value.due_days : '',
    // Where the days count from; a new term guesses from its label as the server does.
    due_basis: value.due_basis || '',
    context: value.context || 'both',
    sort_order: value.sort_order != null ? value.sort_order : '',
  });
  const [error, setError] = useState(null);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async () => {
    try {
      if (hasDays && form.due_days === '') {
        setError(new Error(he.settings.daysRequired));
        return;
      }
      const body = { label_he: form.label_he };
      if (hasDays) {
        body.due_days = Number(form.due_days) || 0;
        body.due_basis = form.due_basis || (/שוטף/.test(form.label_he) ? 'month_end' : 'issue');
      }
      if (hasContext) body.context = form.context;
      if (form.sort_order !== '') body.sort_order = Number(form.sort_order);
      if (isNew) await settingsApi.add(listKey, body);
      else await settingsApi.update(listKey, value.code, body);
      onSaved();
    } catch (e) {
      setError(e);
    }
  };

  return (
    <Modal title={isNew ? he.settings.addValue : he.settings.editValue} onClose={onClose} onSave={submit}>
      {!isNew && (
        <Field label={he.settings.code}>
          <input value={value.code} readOnly />
        </Field>
      )}
      <TextInput label={he.settings.label} value={form.label_he} onChange={set('label_he')} autoFocus />
      {hasDays && (
        <Field label={`${he.settings.days} *`}>
          <input type="number" min="0" step="1" value={form.due_days} onChange={set('due_days')} />
        </Field>
      )}
      {hasDays && (
        <Field label={he.settings.basis}>
          <select
            value={form.due_basis || (/שוטף/.test(form.label_he) ? 'month_end' : 'issue')}
            onChange={set('due_basis')}
          >
            <option value="month_end">{he.settings.bases.month_end}</option>
            <option value="issue">{he.settings.bases.issue}</option>
          </select>
        </Field>
      )}
      {hasContext && (
        <Field label={he.settings.context}>
          <select value={form.context} onChange={set('context')}>
            <option value="job">{he.settings.contexts.job}</option>
            <option value="office">{he.settings.contexts.office}</option>
            <option value="both">{he.settings.contexts.both}</option>
          </select>
        </Field>
      )}
      {error && <ErrorText error={error} />}
    </Modal>
  );
}
